// @givmo/board — add or edit a board seat.
//
// Two fields are required (name, email of record); the person's two facts and the term dates are not.
// Nothing here asks for a Givmo account: a seat is not an account, and `BoardMemberCreate` offers no
// `user_id` field on purpose — an officer-nominated one would let an organization bind a stranger's account
// to its board and misattribute every act recorded afterwards.
//
// THE TWO FACTS (CBG-YR3-ROLES layer 1, D-CBG-21) replaced the old "Seat" picker: Director, yes or no —
// the one that decides votes, quorum and written consents — and the board's titles, several allowed,
// director or not. The form writes `is_director` and `titles` and never the legacy `board_role`, which
// the backend now derives from them. `titles` REPLACES the person's whole set on an edit, so the form
// always sends every title still ticked; while the board's titles can't be read it sends none, and an
// edit then leaves the person's titles as they are. Neither fact authorizes anything: the note under
// them says what everyone on the roster gets.
//
// LAYER 2 (`l2-member-edit-dates`): each ticked title takes the board's own "Held from" / "Held to",
// sent with it (a blank one as `null`, which the backend reads as "no date recorded").
//
// CBG-YR3-TERMS (backend `board-terms.md` §3.2, §13): a seat's terms are recorded and corrected on the
// member's page. "Add a member" keeps its two dates as the seat's FIRST term, with the board's class
// when it has any (a class needs a date); "Edit this seat" no longer offers the dates at all. Under
// the dates, one quiet line says where a founding director's date usually comes from, its detail
// behind "Where to find it" (CBG-UX5-HINT, `term-hint.tsx`); the dates stay optional, and no start
// is ever "the board's founding" without a date (D-CBG-35 (4)).
//
// A SEAT FOR SOMEONE WHO ISN'T ON THE BOARD (CBG-YR6-OUTSIDE, not drawn) is edited for its name and
// address alone: it is never a director and holds no title, so the form neither offers nor sends
// either, nor the board's same-access line, which isn't this seat's.
//
// ADDING THEM TO THE BOARD (CBG-YR6-OUTSIDE-WIDEN, frames w1 and w2; backend
// `board-committee-only-seats.md` §19): such a seat's dialog ends with one line and the way to the act,
// an Owner's as every write here is. The act is the dialog's second view and its own operation
// (`POST …/members/{id}/board-addition`), never part of "Save changes": it opens the board's records,
// earlier ones included, and can't be undone, so it is said before it is done and done by its own
// button. It changes the seat's scope alone — whether they're a director, and any title, are recorded
// afterwards in this dialog, as for anyone on the board (D-CBG-21). What the view says the board's year
// gives them comes from the reads that already serve each part (the board's getting-started list, the
// conflict-of-interest years, the self-assessments, the meetings' calendar blocks), so it names only
// what applies, and a read the console isn't served names nothing.
//
// CBG-AI5-PROPOSE (frame r2; backend `board-ai-proposals.md` §6.1): ADDING FROM A PROPOSAL is this
// same dialog, the same words and the same save, filled from what the organization's connected AI
// proposed: the name, the address, director or not, the term's dates, and the board's titles whose
// names match the proposed ones (case, spacing and Unicode forms ignored, as the console compares
// names). One line under the title says the AI proposed it and to check each detail, with its note
// beneath; a proposed title the board doesn't have is named under the titles, unticked, because a
// title is the board's to create, never the AI's. The save is `create_member` with `from_proposal_id`:
// the guarded add (agent-never item 8), which takes every seat field from this form alone and marks
// the proposal added in the seat's own unit of work.
//
// THE INVITATION, ASKED RIGHT AFTER THE ADD (CBG-UX9, D-CBG-38; `invite-step.tsx`): where the host
// offers it (`offerInvitation`, a holder of `board_member:grant_access`), a create's success turns
// the dialog to its next view, "Invite {name} to the board?", instead of closing — an add from a
// proposal too, since the invitation is a further act of the person's that no connector makes. The
// add has already saved and been said ("Member added to the board."); `onChanged` tells the host so
// the row behind appears. Never on an edit, nor with `withoutTerm` (an election's result, mid-act).
//
// ⚠ EDITING THE EMAIL OF RECORD KEEPS A JOINED MEMBER JOINED (D-CBG-17 decision 2; backend
// `board_roster_service.update_member`): only the address Board writes to moves, and the account that
// joined keeps the seat. Disconnecting it is its own recorded act on the member's page. The form says
// so before they save (CBG-FIX139, D-CBG-38), because the line it replaced said the change
// disconnected them; and the address's own note leaves out the invitation, which a member who has
// joined is never sent.

import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isGivmoError, type OrgBoardMemberOut } from '@givmo/api-client';
import { Button, Dialog, ErrorMessage, Select, TextField, toast } from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { TermHint } from './term-hint';
import { useBoardOperations } from '../../board-runtime';
import { DirectorField, TitlesField, useOfficerTitleCatalog } from './officer-facts-fields';
import {
  heldChoiceKeys,
  heldTitleDates,
  isDirector,
  titleChoices,
  titleRefs,
  type TitleDates,
} from './officer-roles';
import { officerRolesWords as w } from './officer-roles-words';
import { isCommitteeOnlySeat, seatConnection } from './roster-decode';
import { activeClasses, extractDirectorClasses } from './terms-decode';
import { termsWords as tw } from './terms-words';
import { boardJoiningWords as bj, type JoinerFacts } from './board-joining-words';
import { isUpcomingScheduled, useMeetingsById } from '../../calendar/calendar-model';
import { useCoiCycles } from '../conflicts/coi-queries';
import { yearTakingAJoinerOf } from '../conflicts/coi-decode';
import { useSelfAssessments } from '../evaluations/assessment-queries';
import { openAssessmentOf } from '../evaluations/assessment-decode';
import { decodeMeetingCalendar, isKeptInCalendar } from '../meetings/calendar-decode';
import { extractBoardList } from '../onboarding/onboarding-decode';
// CBG-AI5-PROPOSE: the proposal a create is filled from.
import { AiRationale } from '../proposals/ai-rationale';
import { sameTitleName, type MemberProposal } from '../proposals/proposal-decode';
import { proposalWords as pw } from '../proposals/proposal-words';
// CBG-UX9: the invitation, asked once the add has saved.
import { InviteStep, useInviteStep } from './invite-step';

const NO_CLASS = 'none';

/** The act's paragraphs, in the seat dialog's own line style (frame w2). */
const JOIN_LINE = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';

/**
 * What the board's year gives someone added to it today (frame w2): the board's getting-started list
 * when adopted (the roster create's copy), the conflict-of-interest year that takes a joiner's answer,
 * an open self-assessment, and whether the board's upcoming meetings are sent as calendar invitations
 * (the backend's convergence invites them to those).
 */
function useJoinerFacts(orgRef: string, enabled: boolean): JoinerFacts & { calendar: boolean } {
  const operations = useBoardOperations();
  const list = useQuery({
    ...operations.officerGetBoardGettingStartedListOptions({ path: { org_ref: orgRef } }),
    enabled,
    retry: false,
    select: (body: unknown) => extractBoardList(body)?.adopted === true,
  });
  const cycles = useCoiCycles(orgRef, enabled);
  const assessments = useSelfAssessments(orgRef, enabled);
  const meetings = useMeetingsById(orgRef, enabled);
  const year = yearTakingAJoinerOf(cycles.data ?? []);
  const assessment = openAssessmentOf(assessments.data ?? []);
  const calendar = [...meetings.byId.values()].some(
    (meeting) =>
      (meeting.committee_id ?? null) === null &&
      isUpcomingScheduled(meeting) &&
      isKeptInCalendar(decodeMeetingCalendar((meeting as { calendar?: unknown }).calendar)),
  );
  return {
    list: list.data === true,
    coiLabel: year?.label ?? null,
    assessmentLabel: assessment?.label ?? null,
    calendar,
  };
}

export type SeatFormMode = 'create' | 'edit';

export interface SeatFormDialogProps {
  orgRef: string;
  mode: SeatFormMode;
  seat: OrgBoardMemberOut | null;
  onClose: () => void;
  onSaved: () => void;
  /**
   * CBG-YR3-ELECT: "Add someone new" from the election's result. The new seat takes no term here —
   * the result dialog records it — and the dialog hands back the seat it created.
   */
  withoutTerm?: boolean;
  onCreated?: (seat: OrgBoardMemberOut) => void;
  /** CBG-AI5-PROPOSE: the proposal this add is filled from (a create only). */
  proposal?: MemberProposal | null;
  /**
   * CBG-UX9: once a create saves, ask whether to invite the new member instead of closing. The host
   * passes whether the person holds `board_member:grant_access`; never offered on an edit or with
   * `withoutTerm`.
   */
  offerInvitation?: boolean;
  /** CBG-UX9: the seat changed while the dialog stays open (the add, its address, its invitation). */
  onChanged?: () => void;
}

export function SeatFormDialog({
  orgRef,
  mode,
  seat,
  onClose,
  onSaved,
  withoutTerm = false,
  onCreated,
  proposal = null,
  offerInvitation = false,
  onChanged,
}: SeatFormDialogProps) {
  const operations = useBoardOperations();
  const isEdit = mode === 'edit' && seat !== null;
  const outside = isEdit && isCommitteeOnlySeat(seat);
  const asksToInvite = offerInvitation && !isEdit && !withoutTerm;
  const step = useInviteStep(orgRef, () => onChanged?.());

  const [displayName, setDisplayName] = useState(
    seat?.display_name ?? proposal?.display_name ?? '',
  );
  const [email, setEmail] = useState(seat?.email_of_record ?? proposal?.email_of_record ?? '');
  // A new member is recorded as a director unless the officer says otherwise: the backend's own
  // default, and what most people on a board are. A proposal says which it proposed.
  const [director, setDirector] = useState(
    seat === null ? (proposal?.is_director ?? true) : isDirector(seat),
  );
  const titleCatalog = useOfficerTitleCatalog(orgRef);
  const choices = useMemo(
    () => titleChoices(titleCatalog.catalog, isEdit ? seat : null),
    [titleCatalog.catalog, isEdit, seat],
  );
  // Untouched, the boxes show what the person holds now; once ticked or cleared, what the officer chose.
  const [pickedTitles, setPickedTitles] = useState<string[] | null>(null);
  // CBG-AI5-PROPOSE: a proposal's labels tick the board's titles they name; the rest are named under
  // the titles (`unmatchedTitles`), never created here.
  const proposedLabels = useMemo(() => proposal?.title_labels ?? [], [proposal]);
  const proposedKeys = useMemo(
    () =>
      choices
        .filter((choice) => proposedLabels.some((label) => sameTitleName(label, choice.label)))
        .map((choice) => choice.key),
    [choices, proposedLabels],
  );
  const unmatchedTitles =
    titleCatalog.state !== 'ready'
      ? []
      : proposedLabels.filter(
          (label) => !choices.some((choice) => sameTitleName(choice.label, label)),
        );
  const titleKeys =
    pickedTitles ??
    (proposal !== null ? proposedKeys : heldChoiceKeys(choices, isEdit ? seat : null));
  // Layer 2: untouched, each held title's own dates; once edited, what the officer typed.
  const [editedDates, setEditedDates] = useState<Record<string, TitleDates>>({});
  const titleDates = useMemo(
    () => ({ ...heldTitleDates(choices, isEdit ? seat : null), ...editedDates }),
    [choices, isEdit, seat, editedDates],
  );
  // A new seat's first term (CBG-YR3-TERMS): its dates and the board's class, when it has classes.
  const [termStart, setTermStart] = useState(proposal?.term_start_on ?? '');
  const [termEnd, setTermEnd] = useState(proposal?.term_end_on ?? '');
  const [termClass, setTermClass] = useState<string | null>(null);
  const classesQuery = useQuery({
    ...operations.officerListBoardDirectorClassesOptions({ path: { org_ref: orgRef } }),
    enabled: !isEdit && !withoutTerm,
    select: (body: unknown) => activeClasses(extractDirectorClasses(body)),
  });
  const classes = classesQuery.data ?? [];
  const dated = termStart.trim().length > 0 || termEnd.trim().length > 0;
  const [error, setError] = useState<string | null>(null);

  // The backend normalizes the address (trim + lowercase) and holds itself to that, so compare the
  // same way rather than warning about a change that is not one.
  const emailChanged =
    isEdit && email.trim().toLowerCase() !== (seat?.email_of_record ?? '').trim().toLowerCase();
  // A member who has joined with their Givmo account: their address is where Board writes, never
  // where an invitation goes (CBG-FIX139).
  const joinedSeat = isEdit && seat?.user_id !== null && seat?.user_id !== undefined;
  const joinedAddressChange = emailChanged && joinedSeat;

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        display_name: displayName.trim(),
        email_of_record: email.trim(),
        // Someone who isn't on the board: their name and address, nothing of the board's.
        ...(outside ? {} : { is_director: director }),
        // The person's whole set of titles, with each one's dates, or — while the board's titles
        // can't be read — none sent.
        ...(!outside && titleCatalog.state === 'ready'
          ? { titles: titleRefs(choices, titleKeys, titleDates) }
          : {}),
      };
      if (isEdit) {
        await operations.officerUpdateBoardMember({
          path: { org_ref: orgRef, member_id: seat.id },
          body,
          throwOnError: true,
        });
        return null;
      } else {
        const { data } = await operations.officerCreateBoardMember({
          path: { org_ref: orgRef },
          body: {
            ...body,
            term_start_on: termStart.trim().length > 0 ? termStart.trim() : null,
            term_end_on: termEnd.trim().length > 0 ? termEnd.trim() : null,
            // A class only beside a date: the first term needs one (the backend refuses a class alone).
            ...(dated && termClass !== null ? { term_class_id: termClass } : {}),
            // CBG-AI5-PROPOSE: the add completes the proposal it was filled from; every field above
            // is this form's, never the proposal's (design §6.1, N6).
            ...(proposal !== null ? { from_proposal_id: proposal.id } : {}),
          },
          throwOnError: true,
        });
        return data;
      }
    },
    onSuccess: (created) => {
      toast.success(isEdit ? 'Seat updated.' : 'Member added to the board.');
      if (created !== null && created !== undefined) onCreated?.(created);
      if (asksToInvite && created !== null && created !== undefined) {
        onChanged?.();
        step.offer(created);
        return;
      }
      onSaved();
    },
    onError: (caught) => {
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t save that. Please try again.');
    },
  });

  const canSave = displayName.trim().length > 0 && email.trim().length > 0 && !save.isPending;

  // The dialog's second view, for someone who isn't on the board: the act itself (frame w2).
  const [view, setView] = useState<'edit' | 'join'>('edit');
  const [joinError, setJoinError] = useState<string | null>(null);
  const joinFacts = useJoinerFacts(orgRef, outside);
  const joined = isEdit && seatConnection(seat) === 'joined';
  const invited = isEdit && seatConnection(seat) === 'invited';
  const join = useMutation({
    mutationFn: async () => {
      if (!isEdit) return;
      await operations.officerAddBoardMemberToBoard({
        path: { org_ref: orgRef, member_id: seat.id },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(bj.done);
      onSaved();
    },
    onError: (caught) => setJoinError(isGivmoError(caught) ? caught.message : bj.failed),
  });

  // CBG-UX9 (frames s1–s4, p1): the add has saved; the same dialog asks whether to invite them.
  if (step.seat !== null) {
    return (
      <Dialog.Root open onOpenChange={(next) => !next && !step.busy && onClose()}>
        <Dialog.Content
          aria-label={step.title}
          className="flex flex-col gap-16"
          testId="roster-seat-dialog"
        >
          <InviteStep step={step} testIdPrefix="roster-seat" onClose={onClose} />
        </Dialog.Content>
      </Dialog.Root>
    );
  }

  if (isEdit && outside && view === 'join') {
    const joinerLine = bj.joinerLine(joinFacts);
    const lastLine = [
      bj.oneWay,
      joined ? bj.emailed(seat.display_name) : invited ? bj.invited : null,
    ]
      .filter((part): part is string => part !== null)
      .join(' ');
    return (
      <Dialog.Root open onOpenChange={(next) => !next && !join.isPending && onClose()}>
        <Dialog.Content
          aria-label={bj.title(seat.display_name)}
          className="flex flex-col gap-16"
          testId="roster-seat-dialog"
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {bj.title(seat.display_name)}
          </Dialog.Title>
          <p className={JOIN_LINE} data-testid="roster-seat-join-sees">
            {bj.sees}
          </p>
          {joinerLine !== null ? (
            <p className={JOIN_LINE} data-testid="roster-seat-join-joiner">
              {joinerLine}
            </p>
          ) : null}
          {joinFacts.calendar ? (
            <p className={JOIN_LINE} data-testid="roster-seat-join-calendar">
              {bj.calendarLine}
            </p>
          ) : null}
          <p className={JOIN_LINE} data-testid="roster-seat-join-director">
            {bj.directorLater}
          </p>
          <p className={JOIN_LINE} data-testid="roster-seat-join-one-way">
            {lastLine}
          </p>
          {joinError !== null ? (
            <ErrorMessage testId="roster-seat-join-error">{joinError}</ErrorMessage>
          ) : null}
          <div className="flex justify-end gap-10">
            <Button
              appearance="saas"
              tone="secondary"
              disabled={join.isPending}
              onClick={() => {
                setJoinError(null);
                setView('edit');
              }}
              testId="roster-seat-join-back"
            >
              {bj.back}
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              loading={join.isPending}
              onClick={() => {
                setJoinError(null);
                join.mutate();
              }}
              testId="roster-seat-join-confirm"
            >
              {bj.confirm}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label={isEdit ? 'Edit this seat' : 'Add a board member'}
        className="flex flex-col gap-16"
        testId="roster-seat-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {isEdit ? 'Edit this seat' : 'Add a board member'}
        </Dialog.Title>

        {/* CBG-AI5-PROPOSE (frame r2): where this came from, before the fields. */}
        {proposal !== null ? (
          <div className="-mt-6 flex flex-col gap-4" data-testid="roster-seat-proposal">
            <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {pw.formFromProposal}
            </p>
            {/* CBG-FIX160 I (frame i2): the AI gave no email of record; the seat needs one. */}
            {(proposal.email_of_record ?? '').trim().length === 0 ? (
              <p
                className="font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong"
                data-testid="roster-seat-proposal-no-email"
              >
                {pw.formNoEmail}
              </p>
            ) : null}
            <AiRationale note={proposal.source_note} testId="roster-seat-proposal-note" />
          </div>
        ) : null}

        <TextField
          label="Name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          testId="roster-seat-name"
        />

        <TextField
          label="Email of record"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          testId="roster-seat-email"
        />
        {/* CBG-FIX139 (D-CBG-38): the invitation, never the old access link. A member who has
            joined is sent no invitation, so theirs is the approved line without it (approved as
            drafted in CBG-UX-COPY2, B3). */}
        <FieldNote>
          {joinedSeat
            ? 'Where Board sends their emails.'
            : 'Where Board sends their invitation and emails.'}
        </FieldNote>

        {outside ? null : (
          <>
            <DirectorField
              value={director}
              onChange={setDirector}
              disabled={save.isPending}
              testIdPrefix="roster-seat"
            />

            <TitlesField
              state={titleCatalog.state}
              choices={choices}
              selected={titleKeys}
              onChange={setPickedTitles}
              disabled={save.isPending}
              unavailableLine={isEdit ? w.titlesUnavailable : w.titlesUnavailableNew}
              dates={titleDates}
              onDatesChange={(key, dates) =>
                setEditedDates((edited) => ({ ...edited, [key]: dates }))
              }
              testIdPrefix="roster-seat"
            />
            {/* CBG-AI5-PROPOSE (frame r2): a proposed title the board doesn't have. */}
            {proposal !== null
              ? unmatchedTitles.map((label) => (
                  <FieldNote key={label} testId="roster-seat-proposal-title">
                    {pw.titleNotOnBoard(label)}
                  </FieldNote>
                ))
              : null}
            <FieldNote testId="roster-seat-same-access">{w.sameAccess}</FieldNote>
          </>
        )}

        {!isEdit && !withoutTerm ? (
          <>
            <div className="grid grid-cols-2 gap-12">
              <TextField
                label="Term start (optional)"
                type="date"
                value={termStart}
                onChange={(event) => setTermStart(event.target.value)}
                testId="roster-seat-term-start"
              />
              <TextField
                label="Term end (optional)"
                type="date"
                value={termEnd}
                onChange={(event) => setTermEnd(event.target.value)}
                testId="roster-seat-term-end"
              />
            </div>
            {/* CBG-UX5-HINT (frames a1, a2): where a founding director's date comes from. */}
            <TermHint testId="roster-seat-term-hint" />
            {classes.length > 0 ? (
              <Select
                label={tw.classLabel}
                appearance="saas"
                value={termClass ?? NO_CLASS}
                disabled={!dated}
                onValueChange={(next) =>
                  setTermClass(next === null || next === NO_CLASS ? null : next)
                }
                options={[
                  { value: NO_CLASS, label: tw.noClass },
                  ...classes.map((row) => ({ value: row.id, label: row.label })),
                ]}
                testId="roster-seat-term-class"
              />
            ) : null}
          </>
        ) : null}

        {/* CBG-FIX139 (D-CBG-38): a joined member stays joined. The testid keeps its old name. */}
        {joinedAddressChange ? (
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
            data-testid="roster-seat-unlink-note"
          >
            This member has joined with their Givmo account. Changing their email of record changes
            only where Board’s emails go; they keep signing in as before.
          </p>
        ) : null}

        {/* Someone who isn't on the board (frame w1): one line, and the way to the act. */}
        {outside ? (
          <div
            className="flex flex-col items-start gap-4 border-t border-border-subtle pt-14"
            data-testid="roster-seat-board-part"
          >
            <span className={JOIN_LINE}>{bj.notOnTheBoard(seat?.display_name ?? '')}</span>
            <Button
              appearance="saas"
              tone="link"
              size="md"
              disabled={save.isPending}
              onClick={() => {
                setError(null);
                setView('join');
              }}
              testId="roster-seat-add-to-board"
            >
              {bj.addToBoard}
            </Button>
          </div>
        ) : null}

        {error !== null ? <ErrorMessage testId="roster-seat-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="roster-seat-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            disabled={!canSave}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="roster-seat-save"
          >
            {isEdit ? 'Save changes' : 'Add member'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
